import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Member, FeatureAccessSnapshot } from '../types';
import { apiFetch } from '../lib/api';
import { apiUrl } from '../lib/api-base';
import { defaultFeatureAccessSnapshot } from '../lib/feature-access';
import { syncSentryMemberContext } from '../lib/sentry';
import {
  canAccessAdminRoutes as canAccessAdminRoutesFor,
  canAccessOrganizerRoutes as canAccessOrganizerRoutesFor,
  isAdmin as isAdminFor,
  isMember as isMemberFor,
  isOrganizer as isOrganizerFor,
  isUser as isUserFor,
} from '../lib/auth-roles';

interface MeResponse {
  ok: boolean;
  member: Member | null;
  isSuperAdmin?: boolean;
  featureAccess?: FeatureAccessSnapshot;
}

interface AuthContextType {
  member: Member | null;
  loading: boolean;
  isUser: boolean;
  isMember: boolean;
  isAdmin: boolean;
  isOrganizer: boolean;
  isSuperAdmin: boolean;
  canAccessAdminRoutes: boolean;
  canAccessOrganizerRoutes: boolean;
  featureAccess: FeatureAccessSnapshot;
  signIn: () => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [member, setMember] = useState<Member | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [featureAccess, setFeatureAccess] = useState<FeatureAccessSnapshot>(
    defaultFeatureAccessSnapshot(),
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    try {
      const res = await apiFetch<MeResponse>('/api/auth/me');
      setMember(res.member);
      setIsSuperAdmin(res.isSuperAdmin ?? false);
      setFeatureAccess(res.featureAccess ?? defaultFeatureAccessSnapshot());
    } catch {
      if (!options?.silent) {
        setMember(null);
        setIsSuperAdmin(false);
        setFeatureAccess(defaultFeatureAccessSnapshot());
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    syncSentryMemberContext(member, { isSuperAdmin, featureAccess });
  }, [member, isSuperAdmin, featureAccess]);

  useEffect(() => {
    const syncSession = () => void refresh({ silent: true });
    window.addEventListener('focus', syncSession);
    window.addEventListener('smym:feature-access-changed', syncSession);
    return () => {
      window.removeEventListener('focus', syncSession);
      window.removeEventListener('smym:feature-access-changed', syncSession);
    };
  }, [refresh]);

  const signIn = () => {
    window.location.href = apiUrl('/api/auth/google');
  };

  const signOut = async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST', body: '{}' });
    } catch {
      /* still clear locally */
    }
    setMember(null);
    setIsSuperAdmin(false);
    setFeatureAccess(defaultFeatureAccessSnapshot());
    window.location.reload();
  };

  const value = useMemo(
    () => ({
      member,
      loading,
      isUser: isUserFor(member),
      isMember: isMemberFor(member),
      isAdmin: isAdminFor(member),
      isOrganizer: isOrganizerFor(member),
      isSuperAdmin,
      canAccessAdminRoutes: canAccessAdminRoutesFor(member),
      canAccessOrganizerRoutes: canAccessOrganizerRoutesFor(member),
      featureAccess,
      signIn,
      signOut,
      refresh,
    }),
    [member, loading, isSuperAdmin, featureAccess, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
